import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, RevealImage } from "@/components/motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/data/content";

const CAMPUS_DATE = new Date("2026-11-22T09:00:00+11:00");

const weeksAway = () => {
  const weeks = Math.ceil((CAMPUS_DATE - Date.now()) / (7 * 24 * 3600 * 1000));
  if (weeks <= 0) return "Starting now";
  return weeks === 1 ? "1 week away" : `${weeks} weeks away`;
};

const Mentorship = () => {

  return (
    <main data-testid="mentorship-page">
      {/* HEADER */}
      <section className="bg-mist grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <p className="label-text text-ocean" data-testid="mentorship-eyebrow">
            For the next generation of coaches
          </p>
          <h1
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight"
            data-testid="mentorship-heading"
          >
            <MaskedLines lines={["Movement Mentorship"]} />
            <span className="block overflow-hidden pb-[0.1em]">
              <motion.span
                className="block italic font-light text-ocean text-2xl sm:text-3xl lg:text-5xl"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                Get qualified and confident as a personal trainer, beyond the textbook.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 flex flex-wrap items-center gap-4"
          >
            <span
              data-testid="campus-countdown-pill"
              className="inline-flex items-center gap-2 rounded-full border border-ocean/30 bg-ocean/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-ocean"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-ocean" />
              {weeksAway()}
            </span>
            <Link
              to="/contact?interest=PT%20Mentorship"
              data-testid="campus-save-spot-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-ocean"
            >
              Save my spot for 22 November
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <a
              href="https://www.aipt.edu.au/campus-club"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="aipt-learn-more-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:border-ocean hover:text-ocean"
            >
              Learn more with AIPT
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-8 max-w-2xl font-display text-2xl md:text-3xl italic font-light leading-snug text-ink/60"
            data-testid="mentorship-subheading"
          >
            I partner with The Australian Institute of Personal Trainers PT
            course to give you eight Sundays of practical sessions, to comprehend
            and complete your practicals. At the end of it, you will be a qualified
            personal trainer, with real world insights and additional education
            from me in person.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 max-w-xs sm:max-w-sm"
            data-testid="campus-video"
          >
            <div className="overflow-hidden border border-ink/10 shadow-xl shadow-ink/10">
              <video
                src="/videos/pt-campus-web.mp4"
                poster="/videos/pt-campus-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full"
              />
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.25em] text-ink/40">
              Inside a campus session at Peak Physique
            </p>
          </motion.div>
        </div>
      </section>

      {/* SESSION BREAKDOWN */}
      <section className="bg-mist" data-testid="session-breakdown-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-4 pb-24 md:pb-32">
          <Reveal>
            <p className="label-text text-ocean">The eight Sundays</p>
            <h2 className="mt-6 max-w-2xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-balance">
              What each week
              <span className="italic font-light text-ink/50"> looks like</span>
            </h2>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
              The campus runs on AIPT's set weekly timetable: eight hands-on
              sessions inside Peak Physique, with your theory completed online
              through AIPT's learning portal in between.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px border border-ink/10 bg-ink/10 md:grid-cols-2">
            {[
              { n: "01", title: "Anatomy & programming", text: "The foundations: how the body is built and how to write training that works with it." },
              { n: "02", title: "Pre-screening, nutrition & fitness assessments", text: "Getting to know a client properly before their first session." },
              { n: "03", title: "Instructing group fitness", text: "Running a room: energy, structure and coaching a group at once." },
              { n: "04", title: "Working with diverse people", text: "Adapting your coaching to different bodies, backgrounds and needs." },
              { break: true },
              { n: "05", title: "1:1 personal training role plays", text: "Practising real sessions in the room, with feedback as you go." },
              { n: "06", title: "Working with older adults & adolescents", text: "Coaching safely and confidently across the age spectrum." },
              { n: "07", title: "Supporting healthy behaviours & strength and conditioning", text: "The habits and training methods that keep clients progressing." },
              { n: "08", title: "Assess client movement & provide exercise advice", text: "Bringing it together: reading movement and prescribing with confidence." },
            ].map((s, i) =>
              s.break ? (
                <Reveal key="break" className="bg-ink p-8 md:col-span-2 md:p-10">
                  <p className="text-center font-display text-xl md:text-2xl italic font-light text-white/85" data-testid="session-break-note">
                    Then a 12-week break over the holidays: complete your online
                    material and implement what you've been learning.
                  </p>
                </Reveal>
              ) : (
                <Reveal key={s.n} delay={(i % 2) * 0.08} className="bg-white p-8 md:p-10">
                  <div data-testid={`session-week-${s.n}`}>
                    <span className="font-display text-3xl italic font-light text-ocean/50">{s.n}</span>
                    <h3 className="mt-3 font-display text-2xl">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.text}</p>
                  </div>
                </Reveal>
              )
            )}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-center text-sm text-ink/60" data-testid="session-extra-note">
              Plus extra relevant education and insights from me in person, woven
              through every session.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-white" data-testid="mentorship-content-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
                  Mentorship inside The Strength Movement goes beyond programming
                  and movement, it's communication, psychology, navigating food
                  and body image conversations, and developing the judgement to
                  coach the individual in front of you.
                </p>
                <div className="mt-12 grid gap-6 sm:grid-cols-2">
                  <div className="border border-ink/10 bg-mist p-7" data-testid="mentorship-students-card">
                    <span className="font-display text-xl italic text-ocean/70">01</span>
                    <h2 className="mt-2 font-display text-2xl leading-snug">You've been thinking about becoming a personal trainer</h2>
                    <ul className="mt-5 space-y-2.5 text-sm text-ink/70">
                      {["Certificate facilitation & mentoring through the AIPT education course", "Campus & placement support", "Shadowing real coaching sessions"].map((i) => (
                        <li key={i} className="flex items-center gap-3">
                          <span className="h-1 w-1 rounded-full bg-ocean" />{i}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border border-ink/10 bg-mist p-7" data-testid="mentorship-coaches-card">
                    <span className="font-display text-xl italic text-ocean/70">02</span>
                    <h2 className="mt-2 font-display text-3xl">For coaches</h2>
                    <ul className="mt-5 space-y-2.5 text-sm text-ink/70">
                      {[
                        "Programming & movement deep-dives",
                        "Client communication",
                        "Food relationship conversations",
                        "Building your coaching business",
                      ].map((i) => (
                        <li key={i} className="flex items-center gap-3">
                          <span className="h-1 w-1 rounded-full bg-ocean" />{i}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <Link
                  to="/contact"
                  data-testid="mentorship-enquire-button"
                  className="mt-12 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-ocean"
                >
                  Enquire about Mentorship
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <RevealImage
                src="/images/community/mentorship-crew.webp"
                alt="The Strength Movement community crew together at the gym"
                className="aspect-[4/3] w-full"
                testId="mentorship-image"
                delay={0.1}
              />
            </div>
          </div>
        </div>
      </section>

      {/* AIPT PARTNERSHIP */}
      <section className="bg-mist" data-testid="mentorship-aipt-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28">
          <Reveal>
            <div className="grid items-center gap-10 border border-ink/10 bg-white p-8 md:p-14 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <p className="label-text text-ocean">In partnership with AIPT</p>
                <h2 className="mt-4 font-display text-3xl md:text-5xl leading-tight">
                  Qualified, then
                  <span className="italic font-light text-ink/50"> confident.</span>
                </h2>
              </div>
              <div className="lg:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-ink/70">
                <p>
                  Through the AIPT education course, I help facilitate and mentor
                  the personal training certificates, so you learn the craft with
                  someone who is coaching it every week.
                </p>
                <p>
                  And it doesn't stop at graduation. Once you're qualified,
                  ongoing mentorship helps you enter the industry with
                  confidence: real programming conversations, real client
                  scenarios and someone in your corner as you build your coaching
                  practice.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Ready to become the coach"
        accent="you'd want to learn from?"
        body="Send an enquiry and tell me where you're at in your coaching journey, student, new grad, or years in."
      />
    </main>
  );
};

export default Mentorship;
